import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const TRACKING_INTERVAL = 2 * 60 * 1000; // 2 minutes
const SESSION_ID_KEY = 'visitor_session_id';

export const VisitorTracker = () => {
  const location = useLocation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const trackingEnabledRef = useRef<boolean>(true);

  const checkTrackingEnabled = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'visitor_tracking')
        .maybeSingle();

      if (data?.setting_value && typeof data.setting_value === 'object' && 'enabled' in data.setting_value) {
        trackingEnabledRef.current = data.setting_value.enabled as boolean;
      }
    } catch (error) {
      // En cas d'erreur, on garde le tracking activé par défaut
      console.error('Error checking tracking status:', error);
    }
  };

  const getOrCreateSessionId = () => {
    if (sessionIdRef.current) return sessionIdRef.current;
    
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    sessionIdRef.current = sessionId;
    return sessionId;
  };

  const trackVisit = async (pageUrl: string, referrer?: string) => {
    // Vérifier si le tracking est activé
    if (!trackingEnabledRef.current) {
      return;
    }

    // Ne pas appeler track-visit depuis la page challenge (évite la boucle)
    if (pageUrl.startsWith('/challenge')) {
      return;
    }

    try {
      const sessionId = getOrCreateSessionId();

      const { data } = await supabase.functions.invoke('track-visit', {
        body: {
          page_url: pageUrl,
          referrer: referrer || document.referrer,
          session_id: sessionId,
        },
      });

      if (
        data &&
        typeof data === 'object' &&
        (data as { challenge_required?: boolean }).challenge_required === true &&
        sessionStorage.getItem('challenge_passed') !== '1' &&
        !window.location.pathname.startsWith('/challenge')
      ) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/challenge?next=${next}`);
      }
    } catch (error) {
      console.error('Error tracking visit:', error);
    }
  };


  useEffect(() => {
    // Vérifier le statut du tracking au démarrage
    checkTrackingEnabled().then(() => {
      // Track initial page load
      const currentUrl = window.location.pathname + window.location.search;
      trackVisit(currentUrl);

      // Set up periodic tracking to keep session active
      intervalRef.current = setInterval(() => {
        const currentUrl = window.location.pathname + window.location.search;
        trackVisit(currentUrl);
      }, TRACKING_INTERVAL);
    });

    // Écouter les changements du paramètre en temps réel
    const channel = supabase
      .channel('visitor-tracking-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.visitor_tracking'
        },
        (payload) => {
          console.log('Visitor tracking setting changed:', payload);
          if (payload.new && typeof payload.new === 'object' && 'setting_value' in payload.new) {
            const newValue = payload.new.setting_value as { enabled: boolean };
            trackingEnabledRef.current = newValue.enabled;
            console.log('Visitor tracking is now:', trackingEnabledRef.current ? 'enabled' : 'disabled');
          }
        }
      )
      .subscribe((status, err) => {
        if (err) {
          console.debug('Realtime subscription error (non-critical):', err);
        }
      });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      supabase.removeChannel(channel).catch(() => {
        // Suppress channel removal errors
      });
    };
  }, []);

  useEffect(() => {
    // Track route changes
    const currentUrl = location.pathname + location.search;
    trackVisit(currentUrl);
  }, [location.pathname, location.search]);

  return null; // This component doesn't render anything
};
